import React, { useState } from 'react';
import { ArrowUpTrayIcon, XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline';

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
  isReplacing?: boolean;
  isUploading?: boolean;
}

type UploadMode = 'single' | 'multiple';

export const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  isReplacing = false,
  isUploading = false,
}) => {
  const [uploadMode, setUploadMode] = useState<UploadMode>('single');
  const [singleFile, setSingleFile] = useState<File | null>(null);
  const [multipleFiles, setMultipleFiles] = useState<{
    referto: File | null;
    rapporto: File | null;
    allegato: File | null;
    listaCasa: File | null;
    listaOspiti: File | null;
  }>({
    referto: null,
    rapporto: null,
    allegato: null,
    listaCasa: null,
    listaOspiti: null,
  });

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  if (!isOpen) return null;

  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

  const handleSingleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert('Solo file PDF, JPG, JPEG e PNG sono consentiti');
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(`File troppo grande (${(file.size / (1024 * 1024)).toFixed(2)} MB). Massimo 5 MB`);
        return;
      }
      setSingleFile(file);
    }
  };

  const handleMultipleFileChange = (type: keyof typeof multipleFiles, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert('Solo file PDF, JPG, JPEG e PNG sono consentiti');
        return;
      }
      setMultipleFiles(prev => ({ ...prev, [type]: file }));
    }
  };

  const getTotalSize = () => {
    if (uploadMode === 'single') {
      return singleFile ? singleFile.size : 0;
    }
    return Object.values(multipleFiles).reduce((acc, file) => acc + (file?.size || 0), 0);
  };

  const totalSize = getTotalSize();
  const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
  const isOverSize = totalSize > MAX_FILE_SIZE;

  const canUpload = uploadMode === 'single' 
    ? singleFile !== null && !isOverSize
    : Object.values(multipleFiles).some(f => f !== null) && !isOverSize;

  const handleUpload = () => {
    if (!canUpload || isUploading) return;

    const filesToUpload: File[] = [];
    
    if (uploadMode === 'single' && singleFile) {
      filesToUpload.push(singleFile);
    } else {
      // Ordine specifico: referto, lista casa, lista ospiti, rapporto, allegato
      const orderedKeys: (keyof typeof multipleFiles)[] = [
        'referto',
        'listaCasa',
        'listaOspiti',
        'rapporto',
        'allegato'
      ];
      
      orderedKeys.forEach(key => {
        if (multipleFiles[key]) {
          filesToUpload.push(multipleFiles[key]!);
        }
      });
    }

    onUpload(filesToUpload);
  };

  const handleClose = () => {
    if (!isUploading) {
      setSingleFile(null);
      setMultipleFiles({
        referto: null,
        rapporto: null,
        allegato: null,
        listaCasa: null,
        listaOspiti: null,
      });
      setUploadMode('single');
      onClose();
    }
  };

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50' onClick={handleClose}>
      <div className='bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto' onClick={(e) => e.stopPropagation()}>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {isReplacing ? 'Sostituisci Referto' : 'Carica Referto'}
          </h3>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className='p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50'
          >
            <XMarkIcon className='h-6 w-6 text-gray-500' />
          </button>
        </div>

        {isReplacing && (
          <div className='mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg'>
            <p className='text-sm text-yellow-800'>
              ⚠️ <strong>Attenzione:</strong> Il caricamento sostituirà completamente il referto precedente.
            </p>
          </div>
        )}

        <div className='mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
          <p className='text-sm text-blue-800'>
            <strong>Nota:</strong> Puoi caricare PDF o immagini (JPG, PNG). Le immagini verranno convertite automaticamente in PDF.
            Puoi caricare un unico file (consigliato) oppure più documenti separati che verranno automaticamente uniti.
            Dimensione massima totale: <strong>5 MB</strong>
          </p>
        </div>

        {/* Mode Selection */}
        <div className='mb-6'>
          <div className='flex gap-4'>
            <button
              onClick={() => setUploadMode('single')}
              disabled={isUploading}
              className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                uploadMode === 'single'
                  ? 'border-am-600 bg-am-50'
                  : 'border-gray-200 hover:border-gray-300'
              } disabled:opacity-50`}
            >
              <div className='text-center'>
                <DocumentIcon className='h-8 w-8 mx-auto mb-2 text-am-600' />
                <div className='font-semibold text-gray-900'>PDF Unico</div>
                <div className='text-xs text-gray-500 mt-1'>Consigliato</div>
              </div>
            </button>
            <button
              onClick={() => setUploadMode('multiple')}
              disabled={isUploading}
              className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                uploadMode === 'multiple'
                  ? 'border-am-600 bg-am-50'
                  : 'border-gray-200 hover:border-gray-300'
              } disabled:opacity-50`}
            >
              <div className='text-center'>
                <ArrowUpTrayIcon className='h-8 w-8 mx-auto mb-2 text-am-600' />
                <div className='font-semibold text-gray-900'>Documenti Separati</div>
                <div className='text-xs text-gray-500 mt-1'>Verranno uniti</div>
              </div>
            </button>
          </div>
        </div>

        {/* Upload Area */}
        {uploadMode === 'single' ? (
          <div className='mb-6'>
            <label className='block'>
              <div className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                singleFile ? 'border-am-600 bg-am-50' : 'border-gray-300 hover:border-am-400'
              }`}>
                <ArrowUpTrayIcon className='h-12 w-12 mx-auto mb-3 text-gray-400' />
                {singleFile ? (
                  <div>
                    <p className='text-sm font-medium text-gray-900'>{singleFile.name}</p>
                    <p className='text-xs text-gray-500 mt-1'>
                      {(singleFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className='text-sm font-medium text-gray-700'>Clicca per selezionare il file</p>
                    <p className='text-xs text-gray-500 mt-1'>PDF, JPG o PNG - oppure trascina il file qui</p>
                  </div>
                )}
              </div>
              <input
                type='file'
                accept='application/pdf,image/jpeg,image/jpg,image/png'
                className='hidden'
                onChange={handleSingleFileChange}
                disabled={isUploading}
              />
            </label>
          </div>
        ) : (
          <div className='mb-6 space-y-3'>
            {[
              { key: 'referto' as const, label: 'Referto' },
              { key: 'listaCasa' as const, label: 'Lista Squadra Casa' },
              { key: 'listaOspiti' as const, label: 'Lista Squadra Ospiti' },
              { key: 'rapporto' as const, label: 'Rapporto Arbitrale' },
              { key: 'allegato' as const, label: 'Allegato (opzionale)' },
            ].map(({ key, label }) => (
              <div key={key} className='flex items-center gap-3'>
                <label className='flex-1'>
                  <div className='flex items-center gap-3 p-3 border rounded-lg hover:border-am-400 transition-colors cursor-pointer'>
                    <DocumentIcon className='h-5 w-5 text-gray-400' />
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-gray-700'>{label}</p>
                      {multipleFiles[key] && (
                        <p className='text-xs text-gray-500'>
                          {multipleFiles[key]!.name} ({(multipleFiles[key]!.size / 1024).toFixed(0)} KB)
                        </p>
                      )}
                    </div>
                    <span className='text-xs text-am-600 font-medium'>
                      {multipleFiles[key] ? 'Cambia' : 'Seleziona'}
                    </span>
                  </div>
                  <input
                    type='file'
                    accept='application/pdf,image/jpeg,image/jpg,image/png'
                    className='hidden'
                    onChange={(e) => handleMultipleFileChange(key, e)}
                    disabled={isUploading}
                  />
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Size indicator */}
        <div className='mb-6'>
          <div className='flex justify-between text-sm mb-1'>
            <span className='text-gray-600'>Dimensione totale</span>
            <span className={isOverSize ? 'text-red-600 font-semibold' : 'text-gray-900'}>
              {totalSizeMB} MB / 5 MB
            </span>
          </div>
          <div className='w-full bg-gray-200 rounded-full h-2'>
            <div
              className={`h-2 rounded-full transition-all ${
                isOverSize ? 'bg-red-500' : 'bg-am-600'
              }`}
              style={{ width: `${Math.min((totalSize / MAX_FILE_SIZE) * 100, 100)}%` }}
            />
          </div>
          {isOverSize && (
            <p className='text-xs text-red-600 mt-1'>La dimensione totale supera il limite di 5 MB</p>
          )}
        </div>

        {/* Actions */}
        <div className='flex gap-3 justify-end'>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className='px-4 py-2 text-gray-700 bg-white hover:bg-gray-50 rounded-lg transition-colors border border-gray-300 disabled:opacity-50'
          >
            Annulla
          </button>
          <button
            onClick={handleUpload}
            disabled={!canUpload || isUploading}
            className='px-4 py-2 text-white bg-am-600 hover:bg-am-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2'
          >
            {isUploading ? (
              <>
                <div className='h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                Caricamento...
              </>
            ) : (
              <>
                <ArrowUpTrayIcon className='h-5 w-5' />
                Carica
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
